import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Library, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { API, PRIMARY_CATEGORIES } from '@/components/resource-library/resourceLibraryApi';
import { ResourceUploadDialog } from '@/components/resource-library/ResourceUploadDialog';
import { ResourceViewerDialog } from '@/components/resource-library/ResourceViewerDialog';
import { CategoryCard } from './ResourceLibraryPage';
import { PlannerCard } from '@/components/planning/PlannerCard';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

export default function PrimaryResourceLibraryPage() {
  const [years, setYears] = useState([]);
  const [yearsFr, setYearsFr] = useState(null);
  const [lang, setLang] = useState('en');
  const [isAdmin, setIsAdmin] = useState(false);
  const [year, setYear] = useState(null);
  const [subject, setSubject] = useState('');
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadCat, setUploadCat] = useState(null);
  const [viewId, setViewId] = useState(null);
  const token = localStorage.getItem('authToken');
  const reqId = useRef(0);

  useEffect(() => {
    fetch(`${API}/api/resource-library/primary/subjects`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        setYears(d.years || []);
        setYearsFr(d.years_fr || null);
        setLang(d.language || 'en');
        setIsAdmin(!!d.is_admin);
        if (d.years?.length) { setYear(d.years[0].year_level); setSubject(d.years[0].subjects[0]?.subject || ''); }
      })
      .finally(() => setLoading(false));
  }, []);

  const activeYears = lang === 'fr' && yearsFr ? yearsFr : years;
  const current = activeYears.find((y) => y.year_level === year);
  const switchLang = (l) => {
    setLang(l);
    const table = l === 'fr' && yearsFr ? yearsFr : years;
    const y = table.find((x) => x.year_level === year) || table[0];
    if (y) { setYear(y.year_level); setSubject(y.subjects[0]?.subject || ''); }
  };

  const load = useCallback(() => {
    if (!subject || !year) return;
    const id = ++reqId.current;
    fetch(`${API}/api/resource-library/staff?subject=${encodeURIComponent(subject)}&year_level=${year}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => { if (id === reqId.current) setResources(d.resources || []); });
  }, [subject, year]);

  useEffect(() => { load(); }, [load]);

  const pickYear = (y) => {
    setYear(y.year_level);
    if (!y.subjects.some((s) => s.subject === subject)) setSubject(y.subjects[0]?.subject || '');
  };

  const remove = async (res) => {
    if (!window.confirm(`Delete "${res.title}"? Teachers will no longer see it.`)) return;
    const r = await fetch(`${API}/api/resource-library/${res.id}`, { method: 'DELETE', headers: authHeaders() });
    if (r.ok) { toast.success('Deleted'); load(); } else toast.error('Could not delete');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;

  if (!years.length) {
    return (
      <div className="p-6 max-w-3xl mx-auto text-center text-slate-500" data-testid="primary-library-empty">
        No primary year level is linked to your account yet. Ask your principal to assign you a class.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8" data-testid="primary-resource-library-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Library className="h-6 w-6 text-emerald-600" />Primary Resource Library</h1>
        <p className="text-sm text-slate-500 mt-1">Pupil books, activity books, teacher guides, year planners and assessment checklists for Years 1–6, organised by year and subject.</p>
      </div>

      {isAdmin && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm text-emerald-800" data-testid="primary-admin-banner">
          You are the library admin — use the <strong>Upload</strong> buttons to add files. Primary teachers only see View / Download for their year level.
        </div>
      )}

      {yearsFr && (
        <div className="inline-flex rounded-lg border bg-white p-1 text-sm" data-testid="primary-language-toggle">
          <button type="button" onClick={() => switchLang('en')} className={`px-3 py-1.5 rounded-md ${lang === 'en' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="primary-lang-en">English schools</button>
          <button type="button" onClick={() => switchLang('fr')} className={`px-3 py-1.5 rounded-md ${lang === 'fr' ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid="primary-lang-fr">École francophone d'Ambaebulu</button>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="primary-year-tabs">
        {activeYears.map((y) => (
          <button key={y.year_level} type="button" onClick={() => pickYear(y)} data-testid={`primary-year-tab-${y.year_level}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${y.year_level === year ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-700 hover:bg-emerald-50 border-slate-200'}`}>
            Year {y.year_level}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="primary-subject-tabs">
        {current?.subjects.map((s) => (
          <button key={s.subject} type="button" onClick={() => setSubject(s.subject)} data-testid={`primary-subject-tab-${s.subject.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className={`px-3 py-1.5 rounded-md text-sm border transition-colors flex items-center gap-2 ${s.subject === subject ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50 border-slate-200'}`}>
            {s.subject}
            <span className={`text-[11px] rounded-full px-1.5 ${s.subject === subject ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>{s.file_count}</span>
          </button>
        ))}
      </div>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-800" data-testid="primary-library-heading">Year {year} · {subject}</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {PRIMARY_CATEGORIES.map((c) => (
            <div key={c.key} className={c.key === 'year_planner' ? 'md:col-span-2 space-y-3' : ''} data-testid={`primary-section-${c.key}`}>
              <CategoryCard cat={c.key} files={resources.filter((r) => r.category === c.key)} token={token} isAdmin={isAdmin}
                hint={<span className="inline-flex items-center gap-2">{c.hint}{c.staff && <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200"><Lock className="h-3 w-3 mr-1" />Teachers only</Badge>}</span>}
                onUpload={() => setUploadCat(c.key)} onDelete={remove} onOpen={(r) => setViewId(r.id)} />
              {c.key === 'year_planner' && <PlannerCard subject={subject} yearLevel={year} isAdmin={isAdmin} />}
            </div>
          ))}
        </div>
      </section>

      {viewId && <ResourceViewerDialog resources={resources} initialId={viewId} token={token} onClose={() => setViewId(null)} />}
      {uploadCat && (
        <ResourceUploadDialog open onOpenChange={(v) => !v && setUploadCat(null)} subject={subject} yearLevel={year} category={uploadCat} onUploaded={load} />
      )}
    </div>
  );
}
